import { call, put, takeEvery } from 'redux-saga/effects';
import Swal from 'sweetalert2';
import * as types from './reportConfigurationActionType';
import * as actions from './reportConfigurationActions';
import * as services from './reportConfigurationServices';


function* fetchReportConfigurationSaga(action) {
    try {
        const { startDate = '', endDate = '' } = action.payload || {};
        // Call the API - request utility already returns response.data
        const response = yield call(services.getAllReportConfiguration, startDate, endDate);
        if (!response) {
            throw new Error('No response received from API');
        }

        // Dispatch success action with the data
        yield put(actions.fetchReportConfigurationSuccess(response?.values));

    } catch (error) {
        let errorMessage = 'Failed to fetch Performance Dashboard Configurations';

        console.error('Performance Dashboard Configuration API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });

        // Dispatch failure action with error
        yield put(actions.fetchReportConfigurationFailure(errorMessage));
    }
}

function* fetchReportConfigurationByIdSaga(action) {
    try {
        const { ReportConfigurationID } = action.payload;
        // Call the API
        const response = yield call(services.getReportConfigurationById, ReportConfigurationID);
        // Dispatch success action with the values directly (not nested)
        yield put(actions.fetchReportConfigurationByIdSuccess(response?.values || response));
    } catch (error) {
        let errorMessage = 'Failed to fetch Performance Dashboard Configuration details';
        console.error('Fetch Performance Dashboard Configuration By ID API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });
        // Dispatch failure action with error
        yield put(actions.fetchReportConfigurationByIdFailure(errorMessage));
        // Show error notification
        Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#d33'
        });
    }
}

function* saveReportConfigurationSaga(action) {
    try {
        // Call the API
        const response = yield call(services.saveReportConfiguration, action.payload);
        // Dispatch success action
        yield put(actions.saveReportConfigurationSuccess(response));
        // Show success notification
        Swal.fire({
            title: 'Success',
            text: 'Successfully Saved Performance Dashboard Configuration',
            icon: 'success',
            confirmButtonText: 'OK',

            confirmButtonColor: '#8b5cf6',
            showCloseButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
                popup: 'custom-success-popup',
                title: 'custom-success-title',
                content: 'custom-success-content',
                confirmButton: 'custom-success-button'
            }
        });
        // Fetch updated Performance Dashboard Configurations after successful save
        yield put(actions.fetchReportConfigurationRequest());
    }
    catch (error) {
        let errorMessage = 'Failed to save Performance Dashboard Configuration';
        console.error('Save Performance Dashboard Configuration API Error:', {
            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });
        Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#d33'
        });
        // Dispatch failure action with error
        yield put(actions.saveReportConfigurationFailure(errorMessage));
    }
}

function* activateDeactivateReportConfigurationSaga(action) {
    try {
        const { ReportConfigurationID, status, userId } = action.payload;

        // Call the API
        const response = yield call(services.activateDeactivateReportConfiguration, ReportConfigurationID, status, userId);

        // Dispatch success action
        yield put(actions.activateDeactivateReportConfigurationSuccess(response));

        // Show success notification
        Swal.fire({
            title: 'Success',
            text: `Performance Dashboard Configuration has been ${status ? 'activated' : 'deactivated'} successfully`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6366f1',
            timer: 3000,
            timerProgressBar: true
        });

        // Fetch updated Performance Dashboard Configurations list after successful activation/deactivation
        yield put(actions.fetchReportConfigurationRequest());

    } catch (error) {
        let errorMessage = 'Failed to update Performance Dashboard Configuration status';

        console.error('Activate/Deactivate Performance Dashboard Configuration API Error:', {

            status: error.response?.status,
            message: errorMessage,
            data: error.response?.data,
            timestamp: new Date().toISOString()
        });

        // Show error notification
        Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#d33'
        });

        // Dispatch failure action with error
        yield put(actions.activateDeactivateReportConfigurationFailure(errorMessage));
    }
}

function* fetchPowerBIRefreshStatusSaga() {
    try {
        const response = yield call(services.getPowerBIReportsRefresh);
        yield put(actions.fetchPowerBIRefreshStatusSuccess(response?.values));
    } catch (error) {
        console.error('PowerBI Refresh Status API Error:', error);
        yield put(actions.fetchPowerBIRefreshStatusFailure('Failed to fetch refresh status'));
    }
}

function* fetchCapacityDropdownSaga(action) {
    try {
        const { fromDate, toDate, configId } = action.payload;
        const response = yield call(services.getCapacityDropdown, fromDate, toDate, configId);
        
        // Transform the dropdown items to the expected format
        const dropdownItems = response?.values?.dropdownItems || [];
        const transformedData = dropdownItems.map(item => ({
            value: item.Id,
            label: item.Name,
            ...item
        }));
        
        yield put(actions.fetchCapacityDropdownSuccess(transformedData));
    } catch (error) {
        console.error('Capacity Dropdown API Error:', error);
        yield put(actions.fetchCapacityDropdownFailure('Failed to fetch capacity dropdown'));
    }
}

export default function* reportConfigurationSaga() {
    yield takeEvery(types.FETCH_REPORT_CONFIGURATION_REQUEST, fetchReportConfigurationSaga);
    yield takeEvery(types.SAVE_REPORT_CONFIGURATION_REQUEST, saveReportConfigurationSaga);
    yield takeEvery(types.FETCH_REPORT_CONFIGURATION_BY_ID_REQUEST, fetchReportConfigurationByIdSaga);
    yield takeEvery(types.ACTIVATE_DEACTIVATE_REPORT_CONFIGURATION_REQUEST, activateDeactivateReportConfigurationSaga);
    yield takeEvery(types.FETCH_POWERBI_REFRESH_STATUS_REQUEST, fetchPowerBIRefreshStatusSaga);
    yield takeEvery(types.FETCH_CAPACITY_DROPDOWN_REQUEST, fetchCapacityDropdownSaga);
}